import { Stack } from '@mui/material';
import { useIsConfigConstantEqual } from 'hooks/useConfigConstant';
import InconsistenciesCard from './InconsistenciesCard';
import InconsistenciesSlimWarning from './InconsistenciesSlimWarning';
import ClarificationsCarousel from 'components/common/clarifications/ClarificationsCarousel';
import { ElementIssuesContext, SlicerAnswerOutcome } from 'api/element-issues';

interface Props {
    context: ElementIssuesContext;
    onAnswer: (questionId: string, answer: string) => Promise<SlicerAnswerOutcome | void>;
    onSkip: (questionId: string) => Promise<void>;
}

export default function ReportConciergeContent({ context, onAnswer, onSkip }: Props) {
    const showWarningsForViewers = useIsConfigConstantEqual('SHOW_INCONSISTENCY_WARNINGS_FOR_VIEWERS', 'Y');

    const showSlimWarning = !context.isEditAccess && showWarningsForViewers && context.overlappingMeasures.length > 0;

    if (!context.isEditAccess && !showSlimWarning) {
        return null;
    }

    return (
        <Stack
            spacing={1.5}
            sx={{
                flexShrink: 0,
                // Bounded so a tall clarifications card cannot squeeze the conversation
                // area below it down to zero height.
                maxHeight: '60%',
                overflowY: 'auto',
                // The cards clip their own content (overflow: hidden zeroes their flex
                // min-height), so they must be unshrinkable to overflow into the
                // scrollbar instead of being silently cut off.
                '& > *': { flexShrink: 0 },
                p: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}
        >
            {context.isEditAccess && context.inconsistencies.length > 0 && (
                <InconsistenciesCard
                    inconsistencies={context.inconsistencies}
                    canResolve={context.isKgManager === true}
                />
            )}
            {showSlimWarning && <InconsistenciesSlimWarning overlappingMeasures={context.overlappingMeasures} />}
            {context.isEditAccess && context.clarifyingQuestions.length > 0 && (
                <ClarificationsCarousel questions={context.clarifyingQuestions} onAnswer={onAnswer} onSkip={onSkip} />
            )}
        </Stack>
    );
}
